/**
 * Theme-only wishlist.
 *
 * Saved products live in localStorage as a list of product handles, so the
 * feature needs no app, no backend and no customer account. The trade-off is
 * that the list is per-browser: it does not follow the customer to another
 * device. Swapping the storage layer below for an API call is the upgrade
 * path if that ever becomes a requirement.
 *
 * Wrapped in an IIFE with an early return so that executing this file twice
 * is a no-op rather than a crash. Everything below used to sit at the top
 * level, where a second execution hit `const HS_WISHLIST_KEY` again and threw
 * "Identifier 'HS_WISHLIST_KEY' has already been declared" before reaching
 * the customElements guards at the bottom — which were written to survive
 * exactly that case and never got the chance. The tag is loaded once from
 * layout/theme.liquid now, so this is belt-and-braces; it is cheap, and it
 * keeps a future stray include from silently breaking the feature.
 */
(function () {
  if (window.HsWishlist) return;

  const HS_WISHLIST_KEY = 'hs:wishlist';

  const HsWishlist = {
    read() {
      try {
        const raw = JSON.parse(localStorage.getItem(HS_WISHLIST_KEY));
        return Array.isArray(raw) ? raw.filter((h) => typeof h === 'string') : [];
      } catch {
        return [];
      }
    },

    write(handles) {
      try {
        localStorage.setItem(HS_WISHLIST_KEY, JSON.stringify(handles));
      } catch {
        /* private mode / quota — the UI still reflects this session */
      }
      document.dispatchEvent(new CustomEvent('hs:wishlist:change', { detail: { handles } }));
    },

    has(handle) {
      return this.read().includes(handle);
    },

    toggle(handle) {
      const handles = this.read();
      const index = handles.indexOf(handle);
      if (index === -1) handles.push(handle);
      else handles.splice(index, 1);
      this.write(handles);
      return index === -1;
    },

    remove(handle) {
      this.write(this.read().filter((h) => h !== handle));
    },
  };

  window.HsWishlist = HsWishlist;

  /** Heart toggle, used on the product page and on wishlist cards. */
  class HsWishlistButton extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.handle = this.dataset.handle;
      if (!this.button || !this.handle) return;

      this.button.addEventListener('click', () => {
        HsWishlist.toggle(this.handle);
      });

      this.sync = () => this.render();
      document.addEventListener('hs:wishlist:change', this.sync);
      this.render();
    }

    disconnectedCallback() {
      if (this.sync) document.removeEventListener('hs:wishlist:change', this.sync);
    }

    render() {
      const saved = HsWishlist.has(this.handle);
      this.button.setAttribute('aria-pressed', String(saved));
      this.button.classList.toggle('hs-wishlist-button--on', saved);
      const label = saved ? this.dataset.labelRemove : this.dataset.labelAdd;
      if (label) this.button.setAttribute('aria-label', label);
    }
  }

  /**
   * Renders the saved products on the wishlist page. Each card is fetched from
   * the Section Rendering API so prices, badges and markup stay identical to
   * every other product card in the theme instead of being rebuilt in JS.
   */
  class HsWishlistList extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-hs-wishlist-grid]');
      this.empty = this.querySelector('[data-hs-wishlist-empty]');
      this.sectionId = this.dataset.cardSection;

      this.sync = () => this.render();
      document.addEventListener('hs:wishlist:change', this.sync);
      this.render();
    }

    disconnectedCallback() {
      if (this.sync) document.removeEventListener('hs:wishlist:change', this.sync);
    }

    async render() {
      const handles = HsWishlist.read();
      this.toggleEmpty(handles.length === 0);
      if (!handles.length) {
        this.grid.innerHTML = '';
        return;
      }

      const cards = await Promise.all(handles.map((handle) => this.fetchCard(handle)));
      const missing = [];

      this.grid.innerHTML = '';
      cards.forEach((html, index) => {
        if (!html) {
          missing.push(handles[index]);
          return;
        }
        const li = document.createElement('li');
        li.className = 'hs-wishlist__item';
        li.innerHTML = html;
        this.grid.appendChild(li);
      });

      // A saved product can be deleted or unpublished later; drop it silently.
      if (missing.length) {
        HsWishlist.write(handles.filter((h) => !missing.includes(h)));
        return;
      }

      this.toggleEmpty(this.grid.children.length === 0);
    }

    toggleEmpty(isEmpty) {
      if (this.empty) this.empty.hidden = !isEmpty;
      if (this.grid) this.grid.hidden = isEmpty;
    }

    async fetchCard(handle) {
      try {
        const response = await fetch(`${window.Shopify.routes.root}products/${handle}?section_id=${this.sectionId}`);
        if (!response.ok) return null;
        const markup = new DOMParser().parseFromString(await response.text(), 'text/html');
        return markup.querySelector('[data-hs-wishlist-card]')?.innerHTML ?? null;
      } catch {
        return null;
      }
    }
  }

  if (!customElements.get('hs-wishlist-button')) {
    customElements.define('hs-wishlist-button', HsWishlistButton);
  }

  if (!customElements.get('hs-wishlist-list')) {
    customElements.define('hs-wishlist-list', HsWishlistList);
  }
})();
